/* eslint-disable complexity */
import { css, html, nothing } from 'lit';

import { CSS_COLOR_HIGHLIGHT, CSS_FONT_WEIGHT_BOLD } from '../../../styles';
import { calculateRemainder } from '../../controllers/charges/neb-charges-spreadsheet-controller-util';
import NebTableSpreadsheet, {
  ELEMENTS as BASE_ELEMENTS,
} from '../neb-table-spreadsheet';

export const ELEMENTS = {
  ...BASE_ELEMENTS,
};

class NebTableEraEobCharges extends NebTableSpreadsheet {
  static get properties() {
    return {
      ...super.properties,
      patientInsurances: { type: Object },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        /* ERA/EOB specific highlighting styles */
        .cell.highlight-remainder-error,
        .cell.highlight-remainder-error.odd {
          background-color: #f8d7da;
          color: #c62828;
        }

        /* Prevent hover color changes on remainder error cells */
        .row:hover .cell.highlight-remainder-error,
        .row:hover .cell.highlight-remainder-error.odd {
          background-color: #f8d7da !important;
          color: #c62828 !important;
        }

        .cell.highlight-outstanding-error,
        .cell.highlight-outstanding-error.odd {
          background-color: #f8d7da !important;
          color: #c62828 !important;
        }

        .row.row-highlight-remainder-warning .cell:not(.readonly) {
          background-color: #fff3cd;
        }

        .link {
          color: ${CSS_COLOR_HIGHLIGHT};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          text-decoration: underline;
          cursor: pointer;
        }

        .link:focus,
        .link:hover {
          opacity: 0.65;
        }

        /* Payer overlap chip - positioned on right side of cell */
        .payer-overlap-chip {
          position: absolute;
          right: 32px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chip.default {
          background: #e3f2fd;
          color: #1976d2;
          border: 1px solid #bbdefb;
        }

        .chip.default:hover {
          background: #e1f5fe;
          border-color: #81d4fa;
        }

        /* Patient chip styling */
        .chip.patient {
          background: #e8f5e8;
          color: #2e7d32;
          border: 1px solid #81c784;
        }

        .chip.patient:hover {
          background: #e8f5e8;
          border-color: #81c784;
        }

        /* Payer chip styling */
        .chip.payer {
          background: #e3f2fd;
          color: #1976d2;
          border: 1px solid #bbdefb;
        }

        .chip.payer:hover {
          background: #e1f5fe;
          border-color: #81d4fa;
        }

        /* Chip tooltip overlay */
        .chip-overlay {
          position: fixed;
          background: #333;
          color: white;
          padding: 8px 12px;
          border-radius: 4px;
          font-size: 12px;
          line-height: 1.4;
          max-width: 300px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          z-index: 3;
        }

        .chip-overlay-title {
          font-weight: bold;
          font-size: 13px;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #555;
          text-align: center;
        }

        .chip-overlay-content {
          margin-bottom: 2px;
        }

        /* Insurance item styling for tooltips */
        .insurance-item {
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #555;
        }

        .insurance-item:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }

        .insurance-detail {
          margin-bottom: 2px;
        }

        .icon-container {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 2px;
          font-size: 12px;
          z-index: 3;
        }

        .icon {
          width: 20px;
          height: 20px;
          fill: ${CSS_COLOR_HIGHLIGHT};
        }
      `,
    ];
  }

  __initState() {
    super.__initState();

    this.onRemainderChange = () => {};
  }

  // Override getCellClassNames to add ERA/EOB specific highlighting
  getCellClassNames({
    isEditing,
    isEdited,
    isReadOnly,
    isOdd,
    hasIcon,
    item,
    configItem,
    isPopup,
  }) {
    let classes = super.getCellClassNames({
      isEditing,
      isEdited,
      isReadOnly,
      isOdd,
      hasIcon,
      configItem,
      isPopup,
    });

    // Add ERA/EOB specific highlighting for remainder column
    if (configItem?.key === 'remainder') {
      const actualValue = this.getCellValue(item, configItem.key);

      if (actualValue !== 0) {
        classes += ' highlight-remainder-error';
      }
    }

    if (configItem?.key === 'outstanding') {
      const actualValue = this.getCellValue(item, configItem.key);

      if (actualValue < 0) {
        classes += ' highlight-outstanding-error';
      }
    }

    return classes;
  }

  getHeaderCellClassNames({ isPopup, hasChip, hasIcon }) {
    return `cell header-cell ${isPopup ? 'popup' : ''} ${hasIcon ? 'has-icon' : ''} ${hasChip ? 'has-chip' : ''}`;
  }

  renderHeader() {
    if (!this.items.length) return null;

    return html`
      <div class="header" id="${ELEMENTS.header.id}">
        ${this.config.map(config => {
          const isPopup = config.popup === true;
          const hasChip = config.chip ? 'has-chip' : '';
          const headerClass = this.getHeaderCellClassNames({
            isPopup,
            hasChip,
            hasIcon: !!config.icon,
          });

          return html`<div class="${headerClass}">${config.label}</div>`;
        })}
      </div>
    `;
  }

  // Override renderCell to pass additional parameters for highlighting
  // eslint-disable-next-line complexity
  renderCell(item, key, index) {
    const isEditing =
      this.__activeCell &&
      this.__activeCell.id === item.id &&
      this.__activeCell.field === key;

    const isEdited =
      this.__editedCells[item.id] &&
      this.__editedCells[item.id][key] !== undefined;

    const configItem = this.config.find(c => c.key === key);
    const isReadOnly =
      configItem?.editable === false || configItem?.getReadOnly?.(item);
    const isPopup = configItem?.popup === true;
    const isForcePopup = configItem?.getForcePopup?.(item);
    const allowNegative = configItem?.allowNegative === true;
    const isClickable = configItem?.getClickable?.(item);

    const iconProps = configItem?.icon;

    // Use getCellValue to get the actual value (including calculated values)
    const actualValue = this.getCellValue(item, key);
    const displayValue = configItem?.numeric
      ? this.formatNumericValue(key, actualValue)
      : actualValue;

    // Call our specialized getCellClassNames with additional parameters
    const classNames = this.getCellClassNames({
      isEditing,
      isEdited,
      isReadOnly,
      isOdd: item.isOdd,
      isPopup,
      configItem,
      hasIcon: !!iconProps,
      item,
    });

    let spanClassName = '';

    if (isPopup) {
      spanClassName += ' popup';
    }

    if (configItem?.chip && configItem.chip.show(item)) {
      spanClassName += ' has-chip';
    }

    // Get the column index from the config
    const colIndex = this.config.findIndex(c => c.key === key);

    // Custom tooltip for remainder cells with errors
    let tooltipText = '';

    if (key === 'remainder') {
      const remainderValue = this.getCellValue(item, key);

      if (remainderValue !== 0) {
        tooltipText = 'The sum of all yellow columns must = Billed Amount';
      }
    }

    if (key === 'outstanding') {
      const outstandingValue = this.getCellValue(item, key);

      if (outstandingValue < 0) {
        tooltipText = 'Paid amount is greater than debit amount';
      }
    }

    return html`
      <div
        id="${ELEMENTS.cells.get(index, colIndex)}"
        class="${classNames}"
        title="${tooltipText}"
        @click="${e => {
          if (isForcePopup) {
            this.handleExpandClick(e, item, key, isReadOnly);
          } else if (isClickable && configItem?.onClick) {
            configItem.onClick(e, item);
          } else {
            this.handleCellClick(item, key, isReadOnly);
          }
        }}"
      >
        ${isEditing
          ? this.renderCellInput(
              item,
              key,
              displayValue,
              index,
              colIndex,
              isForcePopup,
              isReadOnly,
              allowNegative,
            )
          : html`<span
              title="${displayValue}"
              class="${spanClassName} ${isClickable ? 'link' : ''}"
              >${displayValue}</span
            >`}
        ${!isEditing && this.renderChip(item, configItem)}
        ${isPopup && !isReadOnly && !isEditing
          ? html`
              <div
                class="expand-icon-container"
                tabindex="0"
                @click="${e =>
                  this.handleExpandClick(e, item, key, isReadOnly)}"
                @keydown="${e =>
                  this.handleExpandIconKeydown(e, item, key, isReadOnly)}"
                title="Open Popup"
              >
                <neb-icon class="expand-icon" icon="neb:expand" />
              </div>
            `
          : ''}
        ${iconProps?.renderIcon?.(item)
          ? html`<div
              class="icon-container"
              @click="${e => iconProps.onClick(e, item)}"
            >
              <neb-icon
                class="icon"
                icon="${iconProps.getName
                  ? iconProps.getName(item)
                  : iconProps.name}"
                style=${iconProps.getColor
                  ? `fill: ${iconProps.getColor(item)}`
                  : nothing}
                title="${iconProps.getHoverText?.(item) || nothing}"
              />
            </div>`
          : ''}
      </div>
    `;
  }

  // Override renderRow to add row-level highlighting
  renderRow(item, position, index) {
    // Check if remainder is not 0 for row highlighting
    const remainder = calculateRemainder(item);

    const rowClasses = remainder
      ? 'row row-highlight-remainder-warning'
      : 'row';

    return html`
      <div
        id="row-${index}"
        class="${rowClasses}"
        style="transform: translateY(${position}px)"
      >
        ${this.config.map(configItem =>
          this.renderCell(item, configItem.key, index),
        )}
      </div>
    `;
  }
}

customElements.define('neb-table-era-eob-charges', NebTableEraEobCharges);
